import type { ComponentColorTokens, PaletteColorTokens } from '../../types';
// NOTE: src/apps/guest/packages/joykit/common/theme/shared/colors/paletteTokens.ts
export const getComponentColorTokens = (paletteTokens: PaletteColorTokens): ComponentColorTokens => {
  return {
    backdropFill: 'rgba(0,0,0,.4)',

    // Fill
    fillDark: paletteTokens.primary8, // #3A3BCB
    fillDarkHover: paletteTokens.primary7, // #4A4AFF
    fillDarkActive: paletteTokens.primary9, // #3032A4

    fillLight: paletteTokens.white, // #FFFFFF
    fillLightActive: paletteTokens.mono2, // #F5F5F5
    fillLightHover: paletteTokens.mono1, // #FAFAFA
    // Type
    /* Type (Dark) */
    typePrimaryDark: paletteTokens.mono14, // #0A0A0A // Headline
    typeSecondaryDark: paletteTokens.mono12, // #525252 // Subheads, Descriptions, Body text
    typeTertiaryDark: paletteTokens.mono10, // #737373 // Field Titles
    typeDisabledDark: paletteTokens.mono8, // #737373 // Disabled, Placeholder, Field filler

    /* Type Light */
    typePrimaryLight: paletteTokens.white, // #FFFFFF // Headline
    typeSecondaryLight: paletteTokens.mono4, // #E5E5E5 // Subheads, Descriptions, Body text
    typeTertiaryLight: paletteTokens.mono5, // #D4D4D4 // Field Titles
    typeDisabledLight: paletteTokens.mono8, // #737373 // Disabled, Placeholder, Field filler

    // Buttons
    buttonDisabledText: paletteTokens.mono6, // #A3A3A3
    buttonDisabledFill: paletteTokens.mono4, // #E5E5E5

    buttonText: paletteTokens.mono14, // #0A0A0A
    buttonFill: paletteTokens.mono6, // #A3A3A3
    buttonHover: paletteTokens.mono1, // #FAFAFA
    buttonActive: paletteTokens.mono13, // #525252

    // Primary deprecation phase 1: retargeted from the legacy purple `primary*`
    // palette to the new brand-primary blue (`newUIBrandPrimary`). Phase 2 collapsed
    // the former `buttonNewPrimary*` family into this one. See doc/primary-color-deprecation/PLAN.md.
    buttonPrimaryText: paletteTokens.white, // #FFFFFF
    buttonPrimaryFill: paletteTokens.newUIBrandPrimary, // #4A4AFF
    buttonPrimaryFillLight: paletteTokens.white, // #FFFFFF
    buttonPrimaryFillDark: paletteTokens.newUIBrandPrimary, // #4A4AFF
    buttonPrimaryHover: 'rgba(67, 33, 255, 1)',
    buttonPrimaryActive: 'rgba(32, 0, 211, 1)',
    buttonOutlinePrimaryText: paletteTokens.newUIBrandPrimary,
    buttonOutlinePrimaryTextHover: 'rgba(67, 33, 255, 1)',
    buttonOutlinePrimaryTextPress: 'rgba(32, 0, 211, 1)',
    buttonOutlinePrimaryFillHover: 'rgba(67, 33, 255, 0.1)',
    buttonOutlinePrimaryFillPress: 'rgba(32, 0, 211, 0.1)',

    buttonProductiveText: paletteTokens.white, // #FFFFFF
    buttonProductiveFill: paletteTokens.positive7, // #0F766E, teal-700
    buttonProductiveHover: paletteTokens.positive6, // #0D9488, teal-600
    buttonProductiveActive: paletteTokens.positive8, // #134E4A, teal-900
    buttonOutlineProductiveText: paletteTokens.positive7,
    buttonOutlineProductiveTextHover: paletteTokens.positive6,
    buttonOutlineProductiveTextPress: paletteTokens.positive8,
    buttonOutlineProductiveFillHover: 'rgba(13, 148, 136, 0.1)', // teal-600 (#0D9488) at 10%
    buttonOutlineProductiveFillPress: 'rgba(19, 78, 74, 0.2)', // teal-900 (#134E4A) at 20%

    buttonDestructiveText: paletteTokens.white, // #FFFFFF
    buttonDestructiveFill: paletteTokens.negative6, // #DC2626, red-600
    // Hover and press both darken the fill. The old hover literal (#E00024) sits next to red-600
    // and would read as no change; press is red-800, which the status ramp has no token for.
    buttonDestructiveHover: paletteTokens.negative7, // #B91C1C, red-700
    buttonDestructiveActive: '#991B1B', // red-800
    buttonOutlineDestructiveText: paletteTokens.negative6,
    buttonOutlineDestructiveTextHover: paletteTokens.negative7,
    buttonOutlineDestructiveTextPress: '#991B1B', // red-800
    buttonOutlineDestructiveFillHover: 'rgba(185, 28, 28, 0.1)', // red-700 (#B91C1C) at 10%
    buttonOutlineDestructiveFillPress: 'rgba(153, 27, 27, 0.2)', // red-800 (#991B1B) at 20%

    buttonNeutralText: paletteTokens.white,
    buttonNeutralFill: paletteTokens.mono14,
    // Hover and press both lighten the near-black fill; press sits darker than hover. Neither
    // neutral-700 nor neutral-800 has a palette token. Light 950 / 700 / 800 mirrors the dark
    // 50 / 300 / 200 that design ratified on JW-207 (D4).
    buttonNeutralHover: '#404040', // neutral-700
    buttonNeutralActive: '#262626', // neutral-800
    buttonOutlineNeutralText: paletteTokens.mono14,
    buttonOutlineNeutralTextHover: '#404040', // neutral-700
    buttonOutlineNeutralTextPress: '#262626', // neutral-800
    buttonOutlineNeutralFillHover: 'rgba(82, 82, 82, 0.1)', // neutral-600 (#525252) at 10%
    buttonOutlineNeutralFillPress: 'rgba(82, 82, 82, 0.2)', // neutral-600 (#525252) at 20%

    // Muted: flat neutral fill, no border. Intent-independent.
    buttonMutedText: paletteTokens.mono14, // #0A0A0A
    buttonMutedFill: paletteTokens.mono3, // #E5E5E5
    buttonMutedHover: paletteTokens.mono2, // #F5F5F5 (lighter)
    buttonMutedActive: paletteTokens.mono5, // #D4D4D4 (darker press; mono4 now equals the fill)
    buttonMutedDisabledFill: paletteTokens.mono1, // #FAFAFA

    linkText: paletteTokens.primary7, // #4A4AFF
    linkVisited: paletteTokens.primary9, // #3032A4
    linkHover: paletteTokens.primary6, // #5A69F2
    linkActive: paletteTokens.primary8, // #3A3BCB

    inputFill: paletteTokens.white, // #FFFFFF
    inputFillError: paletteTokens.white, // #FFFFFF
    inputFillDisabled: paletteTokens.mono2, // #F5F5F5
    inputText: paletteTokens.mono14, // #0A0A0A
    inputTextError: paletteTokens.mono14, // #0A0A0A
    inputTextDisabled: paletteTokens.mono10, // #737373
    inputBorder: paletteTokens.mono5, // #D4D4D4
    inputBorderError: paletteTokens.negative6,
    inputBorderDisabled: paletteTokens.mono5, // #D4D4D4
    inputBorderFocused: paletteTokens.mono14, // #0A0A0A
    inputPlaceholder: paletteTokens.mono11, // #525252
    inputPlaceholderDisabled: paletteTokens.mono10, // #737373

    // Selected control: the one colour every selection control reads, so a surface with its own
    // palette (a registration page matched to a website) restyles them all by overriding these three.
    controlSelected: paletteTokens.mono14, // #0A0A0A
    controlSelectedText: paletteTokens.white, // #FFFFFF
    controlSelectedTint: 'rgba(10, 10, 10, 0.04)', // mono14 at 4%

    // Stroke
    stroke: paletteTokens.primary7, // #4A4AFF
    strokeActive: paletteTokens.primary8, // #3A3BCB
    strokeHover: paletteTokens.primary6, // #5A69F2

    // Menu
    menuFill: paletteTokens.white, // #FFFFFF
    menuText: paletteTokens.mono14, // #0A0A0A

    menuItemFillHighlighted: paletteTokens.primary1, // #EEF2FE
    menuItemFillActive: paletteTokens.primary2, // #DFE8FE
    menuItemFillDisabled: paletteTokens.mono3, // #E5E5E5
    menuItemTextHighlighted: paletteTokens.primary7, // #4A4AFF
    menuItemTextActive: paletteTokens.primary8, // #3A3BCB
    menuItemTextDisabled: paletteTokens.mono13, // #525252

    // DatePicker
    datepickerDayHighlighted: paletteTokens.mono12, // #525252
    datepickerDayHighlightedHover: paletteTokens.mono10, // #737373
    datepickerDaySelected: paletteTokens.mono14, // #0A0A0A

    // Scrollbar
    scrollbarHandle: '#9D9D9D',

    // misc
    currentColor: 'currentColor',
  };
};
